import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import Layout from '../../components/layout/Layout';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal';
import EmptyState from '../../components/ui/EmptyState';

// Componente reutilizado para gerenciar tanto categorias quanto marcas
const ListaGerenciavel = ({ titulo, endpoint, loading, itens, onRefresh }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando]   = useState(null);
  const [deletando, setDeletando] = useState(null);
  const [nome, setNome]           = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const abrirNovo = () => {
    setEditando(null);
    setNome('');
    setError('');
    setModalOpen(true);
  };

  const abrirEditar = (item) => {
    setEditando(item);
    setNome(item.nome);
    setError('');
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editando) {
        await api.put(`/${endpoint}/${editando.id}`, { nome });
      } else {
        await api.post(`/${endpoint}`, { nome });
      }
      setModalOpen(false);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const confirmarDelete = async () => {
    if (!deletando) return;
    try {
      await api.delete(`/${endpoint}/${deletando.id}`);
      setDeletando(null);
      onRefresh();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-semibold text-gray-900">{titulo}</h2>
        <button onClick={abrirNovo} className="btn-primary text-xs px-3 py-1.5">
          <PlusIcon className="w-3.5 h-3.5" />
          Novo
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : itens.length === 0 ? (
        <EmptyState title={`Nenhum item cadastrado`} />
      ) : (
        <ul className="divide-y divide-gray-50">
          {itens.map((item) => (
            <li key={item.id} className="flex items-center justify-between py-2.5">
              <span className="text-sm text-gray-800">{item.nome}</span>
              <div className="flex gap-1.5">
                <button
                  onClick={() => abrirEditar(item)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setDeletando(item)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Modal criar/editar */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editando ? `Editar ${titulo.slice(0, -1)}` : `Nova ${titulo.slice(0, -1)}`} size="sm">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome <span className="text-red-500">*</span></label>
            <input value={nome} onChange={(e) => { setNome(e.target.value); setError(''); }} required className="input" autoFocus />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Salvando...' : editando ? 'Salvar' : 'Criar'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal de exclusão */}
      <Modal open={!!deletando} onClose={() => setDeletando(null)} title="Remover item" size="sm">
        <p className="text-sm text-gray-600 mb-6">
          Remover <strong>{deletando?.nome}</strong>?
        </p>
        <div className="flex gap-3 justify-end">
          <button onClick={() => setDeletando(null)} className="btn-secondary">Cancelar</button>
          <button onClick={confirmarDelete} className="btn-danger">Remover</button>
        </div>
      </Modal>
    </div>
  );
};

// Página principal
const Categorias = () => {
  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas]         = useState([]);
  const [loading, setLoading]       = useState(true);

  const fetchTudo = async () => {
    setLoading(true);
    try {
      const [cRes, mRes] = await Promise.all([api.get('/categorias'), api.get('/marcas')]);
      setCategorias(cRes.data);
      setMarcas(mRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTudo(); }, []);

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Categorias e Marcas</h1>
        <p className="text-sm text-gray-500 mt-0.5">Gerencie os tipos e fabricantes de produtos</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ListaGerenciavel
          titulo="Categorias"
          endpoint="categorias"
          loading={loading}
          itens={categorias}
          onRefresh={fetchTudo}
        />
        <ListaGerenciavel
          titulo="Marcas"
          endpoint="marcas"
          loading={loading}
          itens={marcas}
          onRefresh={fetchTudo}
        />
      </div>
    </Layout>
  );
};

export default Categorias;
